import registerImage from "@/common/assets/images/createUser.webp";
import forgotPasswordImage from "@/common/assets/images/forgotPassword.webp";
import loginImage from "@/common/assets/images/login.webp";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { GalleryVerticalEnd, Globe } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface AuthFormProps {
  children: ReactNode;
  image?: string;
  imageAlt?: string;
}

const preloadImages = () => {
  const images = [loginImage, forgotPasswordImage, registerImage];
  images.forEach((src) => {
    const img = new Image();
    img.src = src;
  });
};

export const AuthForm = ({
  children,
  image,
  imageAlt = "Authentication illustration",
}: AuthFormProps) => {
  const [previousImage, setPreviousImage] = useState(image);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    preloadImages();
  }, []);

  useEffect(() => {
    if (image !== previousImage) {
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setPreviousImage(image);
        setIsTransitioning(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [image, previousImage]);

  const { i18n } = useTranslation();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-between">
          <a href="/home" className="flex items-center gap-2 font-medium">
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <GalleryVerticalEnd className="size-4" />
            </div>
            Prop Manage
          </a>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Globe className="h-4 w-4 mr-2" />
                {i18n.language === "uk" ? "Українська" : "English"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => changeLanguage("en")}>
                🇬🇧 English
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeLanguage("uk")}>
                🇺🇦 Українська
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            <div className="flex flex-col gap-6">{children}</div>
          </div>
        </div>
      </div>
      <div className="relative hidden bg-muted lg:block overflow-hidden">
        <img
          key={`previous-${previousImage}`}
          src={previousImage}
          alt={imageAlt}
          className={cn(
            "absolute inset-0 h-full w-full object-cover dark:brightness-[0.8] dark:grayscale-[30%]",
            "transition-all duration-300 ease-in-out",
            isTransitioning ? "scale-110 opacity-0" : "scale-100 opacity-100",
          )}
        />
        <img
          key={`current-${image}`}
          src={image}
          alt={imageAlt}
          className={cn(
            "absolute inset-0 h-full w-full object-cover dark:brightness-[0.8] dark:grayscale-[30%]",
            "transition-all duration-300 ease-in-out",
            isTransitioning ? "scale-100 opacity-100" : "scale-110 opacity-0",
          )}
        />
      </div>
    </div>
  );
};
